import messages from "messages";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { ListGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
// components
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlineDownload } from "assets/svg/outline-download.svg";
import { ReactComponent as OutlinePrint } from "assets/svg/outline-print.svg";
import { ReactComponent as OutlineSend } from "assets/svg/outline-send-2.svg";
import { ReactComponent as OutlineTrash1 } from "assets/svg/outline-trash-1.svg";
import { ReactComponent as OutlineUserCheck } from "assets/svg/outline-user-check.svg";
import ConfirmModal from "components/confirm-modal";
import BillingViewerPdf from "components/Dashboard/components/billing-viewer-pdf";
import IconButton from "components/icon-button";
import LazyImage from "components/lazy-image";
import Loading from "components/Loading";
import querySuperbillByStatementNo from "services/superbill/querySuperbillByStatementNo";
import querySuperbillByUniqueIdForEmail from "services/superbill/querySuperbillByUniqueIdForEmail";
import queryBatchSuperbillByStatementNo from "services/superbill/queryBatchSuperbillByStatementNo";
import queryBatchSuperbillByUniqueIdForEmail from "services/superbill/queryBatchSuperbillByUniqueIdForEmail";
// utils
import { apiHostAndPath } from "utils/api";
import downloadFromDifferentOrigin from "utils/downloadFromDifferentOrigin";
import { useUser } from "contexts/UserContext";
// services
import { useTimeOnPageContext } from "contexts/time-on-page";
import useFetchSuperbillsForAppointments from "hooks/useFetchSuperbillsForAppointments";
import markPatientJoined from "services/scheduler/markPatientJoined";
import finalizeSuperbill from "services/superbill/finalizeSuperbill";
import rejectSuperbill from "services/superbill/rejectSuperbill";
import { AmplitudePageName, track } from "utils/amplitude";
// styles
import styles from "./SuperbillViewer.module.scss";
import getClasses from "utils/getClasses";
import deleteBatchSuperbill from "services/superbill/deleteBatchSuperbill";
import sendSuperbillToPatient from "services/superbill/sendSuperbillToPatient";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import queryAppointment from "services/queryAppointment";

const ORCHID_BUBBLE = "/img/orchid-bubble.png";
const AMPLITUDE_PAGE_NAME: AmplitudePageName = "view-superbill-page";

const SuperbillViewer = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { user } = useUser();
  const navigate = useNavigate();

  const [currAppointmentId, setCurrAppointmentId] = useState("");

  const { statement_no, unique_id_for_email } = useParams<{
    statement_no: string;
    unique_id_for_email: string;
  }>();

  const [batchSuperbill, setBatchSuperbill] = useState<BatchSuperbill>();
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { practiceOwnerOrchidUserId } = useTeamContext();

  useEffect(() => {
    let currentRetryCount = 0;
    const MAX_RETRIES = 10;

    const fetchSuperbill = async () => {
      setIsLoading(true);
      try {
        if (statement_no) {
          const res = await querySuperbillByStatementNo(
            statement_no,
            practiceOwnerOrchidUserId,
          );
          const { appointment_ids } = res;
          setCurrAppointmentId(appointment_ids[0]);
          setIsLoading(false);
        } else if (unique_id_for_email) {
          const res = await querySuperbillByUniqueIdForEmail(
            unique_id_for_email,
          );
          const { appointment_ids } = res;
          setCurrAppointmentId(appointment_ids[0]);
          setIsLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (err?.toString().includes("superbill not found")) {
          fetchBatchSuperbillWithRetry();
        } else {
          setIsLoading(false);
        }
      }
    };

    const fetchBatchSuperbillWithRetry = async () => {
      try {
        if (statement_no) {
          const batch = await queryBatchSuperbillByStatementNo(statement_no);
          setBatchSuperbill(batch);
          setIsLoading(false);
        } else if (unique_id_for_email) {
          const batch = await queryBatchSuperbillByUniqueIdForEmail(
            unique_id_for_email,
          );
          setBatchSuperbill(batch);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching batch superbill:", error);
        if (
          error?.toString() === "Error: superbill not found" &&
          statement_no &&
          currentRetryCount < MAX_RETRIES
        ) {
          currentRetryCount++;
          setRetryCount(currentRetryCount);
          retryTimeoutRef.current = setTimeout(() => {
            fetchBatchSuperbillWithRetry();
          }, 10000);
        } else {
          setIsLoading(false);
        }
      }
    };

    fetchSuperbill();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [statement_no, unique_id_for_email, practiceOwnerOrchidUserId]);

  const [appointments, setAppointments] = useState<AppointmentMetaData[]>([]);

  useEffect(() => {
    if (!currAppointmentId) return;
    queryAppointment(parseInt(currAppointmentId), practiceOwnerOrchidUserId)
      .then((appointment) => setAppointments([appointment]))
      .catch(console.error);
  }, [currAppointmentId, practiceOwnerOrchidUserId]);

  const { appointmentsWithSuperBill } = useFetchSuperbillsForAppointments(
    appointments,
    practiceOwnerOrchidUserId,
  );
  const currAppointment = appointmentsWithSuperBill[0];

  const [superbill, setSuperbill] = useState<SuperBillMetaData>();

  useEffect(() => {
    if (!currAppointment || !user) {
      return;
    }
    const { superbills } = currAppointment;
    if (!superbills[0]) {
      return;
    }
    if (superbills[0].issue_date) {
      setSuperbill(superbills[0]);
    } else {
      if (user.user_type === "orchid_pro") {
        navigate(
          `/pro-dashboard/patient-charts/${superbills[0].patient_cognito_sub}/billing?session_id=${currAppointment.appointment_id}`,
        );
      }
    }
  }, [navigate, user, currAppointment]);

  const fileUrl = useMemo(
    () =>
      batchSuperbill?.statement_no
        ? apiHostAndPath(
            `/api/notes_secure_data/v1/superbill/appointment/pdf/${batchSuperbill.statement_no}-0`,
          )
        : apiHostAndPath(
            `/api/notes_secure_data/v1/superbill/appointment/pdf/${
              superbill?.statement_no
            }-${superbill?.last_revision}${
              practiceOwnerOrchidUserId
                ? `?on_behalf_of=${practiceOwnerOrchidUserId}`
                : ""
            }`,
          ),
    [
      superbill?.statement_no,
      superbill?.last_revision,
      batchSuperbill?.statement_no,
      practiceOwnerOrchidUserId,
    ],
  );

  const onDownload = useCallback(() => {
    downloadFromDifferentOrigin(fileUrl, "superbill.pdf").catch(console.error);
  }, [fileUrl]);

  const [showSharedToPatientModal, setShowSharedToPatientModal] =
    useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [, setShowConfirmExitModal] = useState(false);
  const [showConfirmPaitentJoinedModal, setShowConfirmPaitentJoinedModal] =
    useState(false);

  const onFinalizeSuperbill = useCallback(async () => {
    if (batchSuperbill?.statement_no) {
      try {
        await sendSuperbillToPatient(batchSuperbill.statement_no);
        setShowSharedToPatientModal(true);
      } catch (error) {
        console.error(error);
      }
      return;
    }

    if (!superbill?.statement_no || !superbill?.last_revision) return;
    try {
      await finalizeSuperbill(superbill.statement_no, superbill.last_revision);
      setShowSharedToPatientModal(true);
    } catch (error) {
      if (error?.toString() === "Error: patient did not attend the session") {
        setShowConfirmPaitentJoinedModal(true);
      } else {
        window.alert(error);
      }
    }
  }, [
    superbill?.statement_no,
    superbill?.last_revision,
    batchSuperbill?.statement_no,
  ]);

  const [modalError, setModalError] = useState<any>();

  const onDeleteSuperbill = useCallback(async () => {
    if (batchSuperbill?.statement_no) {
      try {
        await deleteBatchSuperbill(batchSuperbill.statement_no);
        navigate("/pro-dashboard/billing");
      } catch (error) {
        console.error(error);
        setModalError(error);
      }
      return;
    }

    try {
      if (!statement_no) {
        return;
      }
      await rejectSuperbill(statement_no);

      navigate(
        `/pro-dashboard/patient-charts/${currAppointment?.patient?.orchid_user_id}/billing`,
      );
    } catch (error) {
      console.error(error);
      setModalError(error);
    }
  }, [
    navigate,
    currAppointment?.patient?.orchid_user_id,
    statement_no,
    batchSuperbill?.statement_no,
  ]);

  const onConfirmPatientJoined = useCallback(async () => {
    if (!currAppointment?.appointment_id) return;
    try {
      await markPatientJoined(currAppointment.appointment_id);
      setShowConfirmPaitentJoinedModal(false);
      onFinalizeSuperbill();
    } catch (error) {
      console.error(error);
    }
  }, [currAppointment?.appointment_id, onFinalizeSuperbill]);

  return superbill?.orchid_pro_rejected ? null : isLoading ||
    (!superbill?.last_revision && !batchSuperbill?.statement_no) ? (
    <div className={styles["superbill-viewer__loading"]}>
      <Loading />
      {retryCount > 0 && (
        <div className={styles["superbill-viewer__retry-info"]}>
          <p>
            Superbill is being generated. Retrying... (Attempt {retryCount}/10)
          </p>
          <p>
            This may take a few moments while the superbill is being generated.
          </p>
        </div>
      )}
    </div>
  ) : (
    <div className={styles["superbill-viewer"]}>
      {/* confirm patient joined */}
      {showConfirmPaitentJoinedModal && (
        <ConfirmModal
          labelTitle={"Confirm Patient Attendance"}
          children={
            "Our system detects your patient did not attend the session. Please confirm."
          }
          labelConfirm={"Confirm"}
          show={showConfirmPaitentJoinedModal}
          onCancel={() => setShowConfirmPaitentJoinedModal(false)}
          onConfirm={() => onConfirmPatientJoined()}
        />
      )}
      {/* confirm delete superbill */}
      {showConfirmDeleteModal && (
        <ConfirmModal
          labelTitle={messages.areYouSure}
          children={messages.theCurrentSuperbillWillNotBeSaved}
          labelConfirm={messages.delete}
          show={showConfirmDeleteModal}
          onCancel={() => setShowConfirmDeleteModal(false)}
          onConfirm={() => onDeleteSuperbill()}
          error={modalError}
        />
      )}
      {/* confirm close tab */}
      {/* {showConfirmExitModal && (
        <ConfirmModal
          labelTitle={messages.areYouSure}
          children={messages.theCurrentSuperbillWillNotBeSaved}
          labelConfirm={messages.close}
          show={showConfirmExitModal}
          onCancel={() => setShowConfirmExitModal(false)}
          onConfirm={() => window.close()}
        />
      )} */}
      {/* superbill sent */}
      {showSharedToPatientModal && (
        <ConfirmModal
          labelTitle={messages.superbillIsShared}
          children={messages.yourPatientWillReceive}
          labelConfirm={messages.gotIt}
          show={showSharedToPatientModal}
          hideCancel={true}
          onCancel={() => setShowSharedToPatientModal(false)}
          onConfirm={() => setShowSharedToPatientModal(false)}
        />
      )}
      <div className={styles["superbill-viewer__actions"]}>
        <div>
          <IconButton
            className={styles["billing-viewer-title__close"]}
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "exit-page-button", "click");
              if (unique_id_for_email) {
                if (user?.user_type === "orchid_pro") {
                  navigate(
                    `/pro-dashboard/patient-charts/${currAppointment?.patient?.orchid_user_id}/billing?session_id=${currAppointment?.appointment_id}`,
                  );
                } else {
                  navigate("/member-dashboard/billing");
                }
              } else if (superbill?.orchid_pro_finalized) {
                window.close();
              } else {
                setShowConfirmExitModal(true);
              }
            }}
          >
            <OutlineCross />
          </IconButton>
          <span className={styles["billing-viewer-title__contact"]}>
            Superbill for {currAppointment?.patient?.first_name || ""}
            {currAppointment?.patient?.last_name || ""}
          </span>
        </div>
        <div className={styles["superbill-viewer__right-actions"]}>
          {(superbill?.orchid_pro_finalized ||
            batchSuperbill?.sent_to_patient) &&
            user?.user_type === "orchid_pro" && (
              <div className={styles["superbill-viewer__notice"]}>
                <OutlineUserCheck className="green" />
                <div className="ml-2 rg-12 gray01">
                  {messages.superbillSharedToPatient}
                </div>
              </div>
            )}
        </div>
      </div>
      <BillingViewerPdf fileUrl={fileUrl}>
        {!superbill?.orchid_pro_finalized && user?.user_type === "orchid_pro" && (
          <div className={styles["superbill-viewer__floating-note"]}>
            <div className="d-flex">
              <LazyImage src={ORCHID_BUBBLE} width={40} height={40} />
              <span className="ml-2">
                {messages.makeSureProviderAndPracticeInformation}
                {/* <b>{messages.confirmAndShare}</b>. */}
              </span>
            </div>
          </div>
        )}
        <ListGroup className={styles["superbill-viewer__right-floating-note"]}>
          {!superbill?.orchid_pro_finalized &&
            !batchSuperbill?.sent_to_patient &&
            user?.user_type === "orchid_pro" && (
              <ListGroup.Item
                onClick={() => {
                  track(
                    AMPLITUDE_PAGE_NAME,
                    "confirm-and-share-button",
                    "click",
                  );
                  onFinalizeSuperbill();
                }}
                className={styles["superbill-viewer__list-item"]}
              >
                <OutlineSend /> Send to Client
              </ListGroup.Item>
            )}
          <ListGroup.Item
            onClick={(e) => {
              e.preventDefault();
              track(AMPLITUDE_PAGE_NAME, "print-button", "click");
              // open as popup
              window.open(fileUrl, "print", "popup");
            }}
            className={styles["superbill-viewer__list-item"]}
          >
            <OutlinePrint /> Print as PDF
          </ListGroup.Item>
          <ListGroup.Item
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "download-button", "click");
              onDownload();
            }}
            className={styles["superbill-viewer__list-item"]}
          >
            <OutlineDownload /> Download Superbill
          </ListGroup.Item>
          <ListGroup.Item
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "delete-superbill-button", "click");
              setShowConfirmDeleteModal(true);
            }}
            className={getClasses("red", styles["superbill-viewer__list-item"])}
          >
            <OutlineTrash1 /> Delete Superbill
          </ListGroup.Item>
        </ListGroup>
      </BillingViewerPdf>
    </div>
  );
};

export default SuperbillViewer;
